import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import authService from '../../services/auth.service';
import { SignInRequest } from '../../types/auth.types';
import { getAuthErrorMessage } from '../../utils/authErrorHandler';
import AuthShell from './AuthShell';
import AuthField from './AuthField';
import AuthSubmitButton from './AuthSubmitButton';

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<SignInRequest>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const isSubmittingRef = useRef(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = t('auth:signIn.emailRequired');
    }
    if (!formData.password) {
      newErrors.password = t('auth:signIn.passwordRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    if (!validateForm()) return;

    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      await authService.signIn(formData);

      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('userEmail', formData.email);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('userEmail');
      }

      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = getAuthErrorMessage(error, t, 'signIn');
      toast.error(errorMessage);

      if (error.response?.status === 403) {
        const errorDetail = error.response?.data?.detail || '';
        if (
          errorDetail.toLowerCase().includes('verify') &&
          errorDetail.toLowerCase().includes('email')
        ) {
          setTimeout(() => {
            navigate('/verify-email', { state: { email: formData.email } });
          }, 2000);
        }
      }
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem('userEmail');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    if (savedEmail && savedRememberMe) {
      setFormData((prev) => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  return (
    <AuthShell
      title={t('auth:signIn.title')}
      subtitle={
        <>
          {t('auth:signIn.subtitlePrefix')}{' '}
          <Link
            to="/signup"
            className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            {t('auth:signIn.subtitle')}
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthField
          id="email"
          name="email"
          type="email"
          label={t('auth:signIn.emailLabel')}
          value={formData.email}
          onChange={handleInputChange}
          placeholder={t('auth:signIn.emailPlaceholder')}
          error={errors.email}
          icon={EnvelopeIcon}
          required
          autoComplete="email"
        />

        <AuthField
          id="password"
          name="password"
          type="password"
          label={t('auth:signIn.passwordLabel')}
          value={formData.password}
          onChange={handleInputChange}
          placeholder={t('auth:signIn.passwordPlaceholder')}
          error={errors.password}
          icon={LockClosedIcon}
          required
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-white/70 cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-white/20 bg-white/[0.03] text-sky-500 focus:ring-2 focus:ring-sky-500/30 focus:ring-offset-0"
            />
            {t('auth:signIn.rememberMe')}
          </label>
          <Link
            to="/reset-password"
            className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            {t('auth:signIn.forgotPassword')}
          </Link>
        </div>

        <AuthSubmitButton
          isLoading={isLoading}
          loadingLabel={t('auth:signIn.submittingButton')}
        >
          {t('auth:signIn.submitButton')}
        </AuthSubmitButton>
      </form>
    </AuthShell>
  );
};

export default SignIn;
